// Keeps a Microsoft sign-in alive across the two things that were forcing staff
// back through the login page:
//
// 1. msal-browser v4 encrypts its localStorage cache with a key held in a
//    *session* cookie (`msal.cache.encryption`). When the browser session ends
//    (browser restart, iOS killing the tab) the key is gone and MSAL discards
//    every saved account and refresh token on the next load.
// 2. Azure caps refresh tokens issued to SPAs at 24 hours. MSAL's silent
//    fallback is a hidden iframe to login.microsoftonline.com, which Safari,
//    Firefox and Brave block as third-party. The only silent recovery left is a
//    top-level redirect, and it needs a login hint so Microsoft signs the user
//    straight back in instead of showing the account picker.

const LAST_USERNAME_KEY = 'usars:msal-last-username'
const RETURN_PATH_KEY = 'usars:msal-return-path'
const MSAL_ENCRYPTION_COOKIE = 'msal.cache.encryption'
const ENCRYPTION_COOKIE_LIFETIME_MS = 7 * 24 * 60 * 60 * 1000

export function rememberUsername(username: string | undefined | null): void {
  if (!username) return
  try {
    window.localStorage.setItem(LAST_USERNAME_KEY, username)
  } catch {
    // Locked-down storage: the redirect still works, just without a hint.
  }
}

export function forgetUsername(): void {
  try {
    window.localStorage.removeItem(LAST_USERNAME_KEY)
  } catch {
    // ignore
  }
}

export function rememberedLoginHint(): string | undefined {
  try {
    return window.localStorage.getItem(LAST_USERNAME_KEY) || undefined
  } catch {
    return undefined
  }
}

export function saveReturnPath(path: string): void {
  try {
    window.localStorage.setItem(RETURN_PATH_KEY, path)
  } catch {
    // ignore — the user lands on /dashboard instead
  }
}

// Returns the saved in-app path once, then forgets it. Only same-origin
// absolute paths outside the auth routes are honoured.
export function takeReturnPath(): string | null {
  let path: string | null = null
  try {
    path = window.localStorage.getItem(RETURN_PATH_KEY)
    window.localStorage.removeItem(RETURN_PATH_KEY)
  } catch {
    return null
  }
  if (!path || !path.startsWith('/') || path.startsWith('//')) return null
  if (/^\/(login|callback)(?:[/?#]|$)/.test(path)) return null
  return path
}

// Re-issues MSAL's encryption-key cookie with an expiry so the saved sign-in
// survives a browser restart. Called after every MSAL initialize, so the
// window rolls forward while the dashboard is in use.
export function persistMsalCacheKey(now = Date.now()): void {
  const prefix = `${encodeURIComponent(MSAL_ENCRYPTION_COOKIE)}=`
  const rawCookie = document.cookie
    .split(';')
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith(prefix))
  const value = rawCookie?.slice(prefix.length)
  if (!value) return
  const expires = new Date(now + ENCRYPTION_COOKIE_LIFETIME_MS).toUTCString()
  // Same attributes MSAL writes, plus the expiry.
  document.cookie = `${prefix}${value};path=/;SameSite=None;expires=${expires};Secure;`
}
